import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, Download, FileSpreadsheet, FileText, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase, getCurrentUserOrganization, formatSwissCurrency } from '@/integrations/supabase/client';
import { useActiveMandate } from '@/hooks/use-active-mandate';
import type {
  JournalEntryWithBookings,
  BookingEntryWithAccount
} from '@/types/journal-entry';

import {
  calculateTotalDebit as calcDebit,
  calculateTotalCredit as calcCredit,
  calculateTotalVAT as calcVAT,
  getPrimaryDebitAccount as getDebitAccount,
  getPrimaryCreditAccount as getCreditAccount,
} from '@/types/journal-entry';

export const JournalViewer = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<JournalEntryWithBookings | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const { mandate } = useActiveMandate();
  const mandateId = mandate?.id;

  // Fetch accounts for filter
  const { data: accounts = [] } = useQuery({
    queryKey: ['chart-of-accounts', mandateId],
    queryFn: async () => {
      if (!mandateId) return [];
      const org = await getCurrentUserOrganization();
      if (!org) return [];

      const { data, error } = await supabase
        .from('accounts')
        .select('account_number, account_name')
        .eq('organization_id', org.organization?.id ?? org.company_id)
        .eq('mandate_id', mandateId)
        .eq('is_active', true)
        .order('account_number');

      if (error) throw error;
      return data || [];
    },
    enabled: Boolean(mandateId),
  });

  // Fetch journal entries
  const { data: journalEntries = [], isLoading, refetch } = useQuery({
    queryKey: ['journal-entries', mandateId, startDate, endDate, selectedAccount, minAmount, maxAmount, searchTerm],
    queryFn: async () => {
      if (!mandateId) return [];
      const org = await getCurrentUserOrganization();
      if (!org) return [];

      let query = supabase
        .from('journal_entries')
        .select(
          `
          *,
          booking_entries (
            *,
            chart_of_accounts:mandate_chart_of_accounts!booking_entries_account_id_fkey (
              account_number,
              account_name,
              account_type
            )
          )
        `,
        )
        .eq('mandate_id', mandateId)
        .eq('status', 'posted')
        .order('journal_number', { ascending: false });

      if (startDate) {
        query = query.gte('value_date', format(startDate, 'yyyy-MM-dd'));
      }

      if (endDate) {
        query = query.lte('value_date', format(endDate, 'yyyy-MM-dd'));
      }

      // Note: Account filtering now needs to check booking_entries
      // For simplicity, we'll filter client-side if selectedAccount is set

      const { data, error } = await query;
      if (error) throw error;

      // Client-side filtering for amount, search, and account
      let filtered = data || [];

      // Filter by account (check booking_entries)
      if (selectedAccount !== 'all') {
        filtered = filtered.filter(entry => 
          entry.booking_entries?.some((be: any) => 
            be.account_number === selectedAccount
          )
        );
      }

      if (minAmount) {
        const min = parseFloat(minAmount);
        filtered = filtered.filter(entry => {
          // Calculate total debit amount from booking_entries
          const totalDebit = entry.booking_entries?.reduce((sum: number, be: any) => 
            sum + (be.debit_amount || 0), 0) || 0;
          return Math.abs(totalDebit) >= min;
        });
      }

      if (maxAmount) {
        const max = parseFloat(maxAmount);
        filtered = filtered.filter(entry => {
          const totalDebit = entry.booking_entries?.reduce((sum: number, be: any) => 
            sum + (be.debit_amount || 0), 0) || 0;
          return Math.abs(totalDebit) <= max;
        });
      }

      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(entry =>
          entry.description?.toLowerCase().includes(search) ||
          entry.reference_number?.toLowerCase().includes(search) ||
          entry.booking_entries?.some((be: any) =>
            be.account_number?.toLowerCase().includes(search) ||
            be.description?.toLowerCase().includes(search)
          )
        );
      }

      return filtered;
    },
    enabled: Boolean(mandateId),
  });

  // Export to Excel mutation - properly handles nested booking_entries
  const exportExcelMutation = useMutation({
    mutationFn: async () => {
      const rows: string[] = [
        ['Buchungsnr.', 'Datum', 'Valuta', 'Sollkonto', 'Habenkonto', 'Betrag', 'MWST', 'Total', 'Beschreibung'].join(',')
      ];
      
      journalEntries.forEach((entry: JournalEntryWithBookings) => {
        const totalDebit = calcDebit(entry);
        const totalVAT = calcVAT(entry);
        const debitAccount = getDebitAccount(entry);
        const creditAccount = getCreditAccount(entry);
        
        rows.push([
          entry.journal_number?.toString() || '',
          format(new Date(entry.booking_date), 'dd.MM.yyyy'),
          format(new Date(entry.value_date), 'dd.MM.yyyy'),
          debitAccount ? `${debitAccount.account_number} - ${debitAccount.account_name}` : '',
          creditAccount ? `${creditAccount.account_number} - ${creditAccount.account_name}` : '',
          totalDebit.toFixed(2),
          totalVAT.toFixed(2),
          (totalDebit + totalVAT).toFixed(2),
          `"${entry.description?.replace(/"/g, '""') || ''}"`,
        ].join(','));
      });

      const csv = rows.join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Journal_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Excel-Export erfolgreich');
    },
  });

  // Export to PDF mutation
  const exportPDFMutation = useMutation({
    mutationFn: async () => {
      // In production, this would call an edge function to generate PDF
      toast.success('PDF-Export wird vorbereitet...');
      
      // Simulate PDF download
      setTimeout(() => {
        toast.success('PDF erfolgreich exportiert');
      }, 1000);
    },
  });

  const handleEntryClick = (entry: JournalEntryWithBookings) => {
    setSelectedEntry(entry);
    setDetailDialogOpen(true);
  };

  const clearFilters = () => {
    setStartDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    setEndDate(new Date());
    setSelectedAccount('all');
    setMinAmount('');
    setMaxAmount('');
    setSearchTerm('');
  };

  // Calculate totals from booking_entries using type-safe helpers
  const totalDebit = journalEntries.reduce((sum, entry) => sum + calcDebit(entry), 0);
  const totalCredit = journalEntries.reduce((sum, entry) => sum + calcCredit(entry), 0);

  if (!mandateId) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          Kein aktives Mandat gefunden. Bitte Mandat auswählen.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-brand-primary">Journal</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => exportExcelMutation.mutate()}
              disabled={journalEntries.length === 0 || exportExcelMutation.isPending}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Excel Export
            </Button>
            <Button
              variant="outline"
              onClick={() => exportPDFMutation.mutate()}
              disabled={journalEntries.length === 0 || exportPDFMutation.isPending}
            >
              <FileText className="mr-2 h-4 w-4" />
              PDF Export
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range */}
          <div className="space-y-2">
            <Label>Von Datum</Label>
            <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'dd.MM.yyyy', { locale: de }) : 'Wählen...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => {
                    setStartDate(date);
                    setStartDateOpen(false);
                  }}
                  locale={de}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Bis Datum</Label>
            <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, 'dd.MM.yyyy', { locale: de }) : 'Wählen...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => {
                    setEndDate(date);
                    setEndDateOpen(false);
                  }}
                  locale={de}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Account Filter */}
          <div className="space-y-2">
            <Label>Konto</Label>
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Konten</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.account_number} value={account.account_number}>
                    {account.account_number} - {account.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search */}
          <div className="space-y-2">
            <Label>Suche</Label>
            <Input
              placeholder="Beschreibung, Buchungsnr..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Amount Range */}
          <div className="space-y-2">
            <Label>Min. Betrag (CHF)</Label>
            <Input
              type="number"
              step="0.05"
              placeholder="0.00"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Max. Betrag (CHF)</Label>
            <Input
              type="number"
              step="0.05"
              placeholder="Unbegrenzt"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
            />
          </div>

          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">
              <Filter className="mr-2 h-4 w-4" />
              Filter zurücksetzen
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Anzahl Buchungen</div>
          <div className="text-2xl font-bold text-brand-primary">{journalEntries.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Soll</div>
          <div className="text-2xl font-bold text-blue-600">{formatSwissCurrency(totalDebit)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Haben</div>
          <div className="text-2xl font-bold text-green-600">{formatSwissCurrency(totalCredit)}</div>
        </Card>
      </div>

      {/* Journal Table */}
      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-2 font-semibold text-sm">Buchungsnr.</th>
                <th className="text-left py-3 px-2 font-semibold text-sm">Datum</th>
                <th className="text-left py-3 px-2 font-semibold text-sm">Sollkonto</th>
                <th className="text-left py-3 px-2 font-semibold text-sm">Habenkonto</th>
                <th className="text-left py-3 px-2 font-semibold text-sm">Beschreibung</th>
                <th className="text-right py-3 px-2 font-semibold text-sm">Betrag</th>
                <th className="text-right py-3 px-2 font-semibold text-sm">MWST</th>
                <th className="text-right py-3 px-2 font-semibold text-sm">Total</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-muted-foreground">
                    Lädt...
                  </td>
                </tr>
              ) : journalEntries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-muted-foreground">
                    Keine Buchungen gefunden
                  </td>
                </tr>
              ) : (
                journalEntries.map((entry) => {
                  // Use type-safe helpers to extract data
                  const totalDebit = calcDebit(entry);
                  const totalCredit = calcCredit(entry);
                  const debitEntry = getDebitAccount(entry);
                  const creditEntry = getCreditAccount(entry);
                  
                  return (
                    <tr
                      key={entry.id}
                      className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleEntryClick(entry)}
                    >
                      <td className="py-3 px-2">
                        <span className="font-mono text-sm">{entry.journal_number || '-'}</span>
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {format(new Date(entry.value_date), 'dd.MM.yyyy')}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            {debitEntry?.account_number || '-'}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            {creditEntry?.account_number || '-'}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm truncate max-w-[200px]" title={entry.description}>
                        {entry.description}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-sm">
                        {formatSwissCurrency(totalDebit)}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-sm text-muted-foreground">
                        {formatSwissCurrency(totalCredit)}
                      </td>
                      <td className="py-3 px-2 text-right font-mono font-semibold text-sm">
                        {formatSwissCurrency(totalDebit)} {/* Debit = Credit in balanced entry */}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Buchungsdetails</DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Buchungsnummer</Label>
                  <p className="font-mono font-semibold">{selectedEntry.journal_number || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Badge className="bg-green-100 text-green-800">Gebucht</Badge>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Buchungsdatum</Label>
                  <p className="font-semibold">
                    {format(new Date(selectedEntry.booking_date), 'dd.MM.yyyy', { locale: de })}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Valutadatum</Label>
                  <p className="font-semibold">
                    {format(new Date(selectedEntry.value_date), 'dd.MM.yyyy', { locale: de })}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Sollkonto</Label>
                    {(() => {
                      const debitEntry = getDebitAccount(selectedEntry);
                      return debitEntry ? (
                        <>
                          <p className="font-mono font-semibold">{debitEntry.account_number}</p>
                          <p className="text-sm text-muted-foreground">{debitEntry.account_name || '-'}</p>
                        </>
                      ) : <p className="text-sm text-muted-foreground">-</p>;
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Habenkonto</Label>
                    {(() => {
                      const creditEntry = getCreditAccount(selectedEntry);
                      return creditEntry ? (
                        <>
                          <p className="font-mono font-semibold">{creditEntry.account_number}</p>
                          <p className="text-sm text-muted-foreground">{creditEntry.account_name || '-'}</p>
                        </>
                      ) : <p className="text-sm text-muted-foreground">-</p>;
                    })()}
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground">Beschreibung</Label>
                <p className="mt-1">{selectedEntry.description}</p>
              </div>

              <div className="border-t pt-4">
                <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-800">Betrag:</span>
                    <span className="font-mono font-semibold text-blue-900">
                      {formatSwissCurrency(Math.abs(calcDebit(selectedEntry)))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-800">MWST:</span>
                    <span className="font-mono font-semibold text-blue-900">
                      {formatSwissCurrency(calcVAT(selectedEntry))}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-300">
                    <span className="font-semibold text-blue-900">Total:</span>
                    <span className="font-mono font-bold text-blue-900 text-lg">
                      {formatSwissCurrency(Math.abs(calcDebit(selectedEntry)) + calcVAT(selectedEntry))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 text-xs text-muted-foreground">
                <p>Erstellt am: {format(new Date(selectedEntry.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
